import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation } from "react-router-dom";
import CompanyLogo from "../../../assets/svgIcons/company-logo-sidebar.svg";
import { setSelectedIndex } from "../../../services/redux/slices/tabsSlice";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import { RootState } from "../../../services/redux/store";
import { setHorizontalSteps, setIsBypassLogin } from "../../../services/redux/slices/stepperDynamicFormSlice";
import { formatISOToDDMMYYYY } from "../../../utils/dateFormatUtils";

export interface MenuItem {
  title: string;
  path?: string;
  activeIcon?: React.ElementType | string;
  inActiveIcon?: React.ElementType | string;
  subMenus?: MenuItem[];
}

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  handleLogOutMenu: () => void;
  menuItems: MenuItem[];
}

// Helper functions
const isPathActive = (
  currentPath: string,
  item: MenuItem,
  selectedIndex: number
) => {
  const itemPath = item.path || "";

  if (itemPath.startsWith("http")) return false;

  if (currentPath === "/dashboard" && (itemPath === "/dashboard" || itemPath === "")) {
    const title = item.title?.trim();
    if (title === "Dashboard" || title === "Loans") return selectedIndex === 0;
    if (title === "Disbursement" || title === "Withdraw") return selectedIndex === 1;
    if (title === "Payment") return selectedIndex === 2;
    if (title === "Documents") return selectedIndex === 3;
    if (title === "History") return selectedIndex === 4;
    
    return false;
  }

  return currentPath === itemPath;
};

const hasActiveChild = (
  currentPath: string,
  item: MenuItem,
  selectedIndex: number
): boolean => {
  if (item.subMenus) {
    return item.subMenus.some(
      (subItem) =>
        isPathActive(currentPath, subItem, selectedIndex) ||
        hasActiveChild(currentPath, subItem, selectedIndex)
    );
  }
  return false;
};

const getInitialOpenSubmenu = (
  menuItems: MenuItem[],
  currentPath: string,
  selectedIndex: number
): string | null => {
  const activeParent = menuItems.find((item) =>
    item.subMenus ? hasActiveChild(currentPath, item, selectedIndex) : false
  );
  return activeParent?.title || null;
};

const MaskedIcon = ({ src, color, size = 24 }: { src: string; color: string; size?: number }) => (
  <Box
    component="span"
    sx={{
      display: "block",
      width: size,
      height: size,
      backgroundColor: color,
      maskImage: `url(${src})`,
      maskRepeat: "no-repeat",
      maskPosition: "center",
      maskSize: "contain",
      WebkitMaskImage: `url(${src})`,
      WebkitMaskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      WebkitMaskSize: "contain",
    }}
  />
);

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  handleLogOutMenu,
  menuItems,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = location.pathname;
  const selectedIndex = useSelector((state: any) => state.tabs.selectedIndex);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(getInitialOpenSubmenu(menuItems, currentPath, selectedIndex));
  const selectedLoanNumber = useSelector((state: RootState) => state.dashboardData.selectedLoanNumber);
  const dashboardDataSet = useSelector((state: RootState) => state.dashboardData.sanctionedDisbursedData);
  const termLoanData = useSelector((state: RootState) => state?.auth?.loanCategory); 
  const tabType = useSelector((state: RootState) => state.tabs.tabType);

  useEffect(() => {
    const requiredOpenSubmenu = getInitialOpenSubmenu(menuItems, currentPath, selectedIndex);
    if (requiredOpenSubmenu && requiredOpenSubmenu !== openSubmenu) {
      setOpenSubmenu(requiredOpenSubmenu);
    }
  }, [currentPath, selectedIndex]);

  const handleSubmenuClick = (title: string) => {
    dispatch(setIsBypassLogin(false));
    setOpenSubmenu(openSubmenu === title ? null : title);
  };

  const checkWithdrawal = (loanAccountNumber: any) => {
    let filterLoanAccountNumber = dashboardDataSet?.filter((acc: any) => {
      if (loanAccountNumber === acc.loanAccountNumber) {
        return acc;
      }
    });

    const nextRepayDate = new Date(filterLoanAccountNumber?.[0]?.nextRepayDate);
    const nextRepayDay = nextRepayDate.getDate(); // e.g. 19

    const currentDate = new Date();
    const currentDay = currentDate.getDate();

    const minDay = nextRepayDay - 2; // 19 - 2 = 17

    // Returns true only if currentDay falls between nextRepayDay and nextRepayDay - 2
    const isValidDate = currentDay >= minDay && currentDay <= nextRepayDay;

    return {
      // message: `Withdrawal requests are restricted between the ${minDay}th to ${nextRepayDay}th of every month. Please try again after the ${nextRepayDay}.`,
      message: `Withdrawal requests are disabled between ${minDay} and ${nextRepayDay} of every month. Please submit your request after your EMI date ${formatISOToDDMMYYYY(nextRepayDate)}.`,
      isValidDate,
    };
  };

  const handleItemClick = (item: MenuItem) => {
    if(item?.path === "/application/existing-application") {
      dispatch(setHorizontalSteps([]));
    }
    const { isValidDate, message } = checkWithdrawal(selectedLoanNumber);
    if(item.title === "Withdraw" && isValidDate) {
      dispatch(
        setStatusPopup({
          status: "Uh-oh! Withdrawal is on hold!",
          message: message,
        })
      );
      return;
    }
    onClose();
    setOpenSubmenu(null);
    dispatch(setIsBypassLogin(false));
    const targetPath = item.path || "";

    if (targetPath.startsWith("http")) {
      window.open(targetPath, "_blank", "noopener,noreferrer");
      return;
    }

    if (targetPath === "" || targetPath === "/dashboard") {
      let newIndex = 0;
      let tabTypeParam = "";
      const title = item.title?.trim();

      if (title === "Disbursement") {
        const matchVal = dashboardDataSet?.find((data: any) => data?.lanNumber === selectedLoanNumber);
        if (matchVal?.isExistDisbursementAllowdLoan === false || matchVal?.status != "A") {
          newIndex = 0;
          dispatch(
            setStatusPopup({
              status: "no access",
              message: "Disbursement requests are currently unavailable for this loan",
            })
          );
        } else {
          newIndex = 1;
        }
      } else if (title === "Payment") {
        if (termLoanData?.TermLoan?.length > 0 && tabType === "Term Loan") {
          var matchVal = dashboardDataSet?.find((data: any) => data?.lanNumber === selectedLoanNumber);
          if (matchVal?.status != "A") {
            newIndex = 0;
            dispatch(
              setStatusPopup({
                status: "no access",
                message: "Payment requests are currently unavailable for this loan",
              })
            );
          } else {
            newIndex = 2;
          }
        } else if (termLoanData?.Flexi?.length > 0 && tabType === "Flexi Term Loan") {
          var matchVal = dashboardDataSet?.find((data: any) => data?.loanAccountNumber === selectedLoanNumber);
          if (matchVal?.statusOfLoan != "A") {
            newIndex = 2;
            dispatch(
              setStatusPopup({
                status: "no access",
                message: "Payment requests are currently unavailable for this loan",
              })
            );
          } else {
            newIndex = 2;
          }
        }
      } else if (title === "Documents") {
        newIndex = 3;
      } else if (title === "History") {
        newIndex = 4;
      } else if (title === "Withdraw") {
        newIndex = 1;
        if (tabType === "Flexi Term Loan") {
          tabTypeParam = "withraw";
        }
      } else {
        newIndex = 0;
        if (tabType === "Flexi Term Loan") {
          tabTypeParam = "accountsummary";
        }
      }

      if (tabType === "Flexi Term Loan") {
        const tabTypeByIndex: Record<number, string> = {
          0: "accountsummary",
          1: "withraw",
          2: "payment",
          3: "statements",
        };
        tabTypeParam = tabTypeByIndex[newIndex] || "accountsummary";
      }
    
      dispatch(setSelectedIndex(newIndex));
      navigate(tabTypeParam ? `/dashboard?tabType=${tabTypeParam}` : "/dashboard");
    } else {
      navigate(targetPath);
    }
  };

  const activeStyles = {
    backgroundColor: "#e0e0e0",
    "&:hover": { backgroundColor: "#d0d0d0" },
  };

  const defaultHoverStyle = {
    "&:hover": { backgroundColor: "#f5f5f5" },
  };

  const renderMenuItems = (items: MenuItem[], depth = 0) => {
    return items.map((item) => {
      const isActive = isPathActive(currentPath, item, selectedIndex);
      const isParentActive = hasActiveChild(currentPath, item, selectedIndex);
      const paddingLeft = 16 + depth * 24;
      const hasSubMenus = item.subMenus && item.subMenus.length > 0;
      const ACTIVE_COLOR = "#00B3B5";
      const INACTIVE_COLOR = "#1C1C1E";
      const currentColor = (hasSubMenus ? isParentActive : isActive) ? ACTIVE_COLOR : INACTIVE_COLOR;
      const CurrentIcon = (hasSubMenus ? isParentActive : isActive) ? item.activeIcon : item.inActiveIcon;
      const iconSize = 24;

      const renderIconContent = () => {
        if (!CurrentIcon) return null;

        if (typeof CurrentIcon === 'string') {
          return <MaskedIcon src={CurrentIcon} color={currentColor} size={iconSize} />;
        }

        return (
          <Box 
            component={CurrentIcon} 
            sx={{ 
              color: currentColor, 
              width: iconSize, 
              height: iconSize,
              fill: 'currentColor'
            }} 
          />
        );
      };

      return (
        <React.Fragment key={item.title}>
          <ListItem disablePadding>
            {hasSubMenus ? (
              <Box sx={{ width: "100%" }}>
                <ListItemButton
                  onClick={() => handleSubmenuClick(item.title)}
                  sx={{
                    pl: `${paddingLeft}px`,
                    ...defaultHoverStyle,
                  }}
                >
                  {depth === 0 && CurrentIcon && (
                    <ListItemIcon sx={{ minWidth: "40px" }}>
                      {renderIconContent()}
                    </ListItemIcon>
                  )}

                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: '15px',
                      fontWeight: 400,
                      color: currentColor,
                    }}
                  />
                  {openSubmenu === item.title ? (
                    <ExpandLess sx={{ color: currentColor }} />
                  ) : (
                    <ExpandMore sx={{ color: currentColor }} />
                  )}
                </ListItemButton>
                <Collapse
                  in={openSubmenu === item.title}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {renderMenuItems(item.subMenus || [], depth + 1)}
                  </List>
                </Collapse>
              </Box>
            ) : (
              <ListItemButton
                onClick={() => handleItemClick(item)}
                sx={{
                  pl: `${paddingLeft}px`,
                  ...(isActive ? activeStyles : defaultHoverStyle),
                }}
              >
                {depth === 0 && CurrentIcon && (
                  <ListItemIcon sx={{ minWidth: "40px" }}>
                    {renderIconContent()}
                  </ListItemIcon>
                )}

                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    color: currentColor,
                    fontWeight: 400,
                    fontSize: '14px',
                  }}
                />
              </ListItemButton>
            )}
          </ListItem>

          {depth === 0 && <Divider />}
        </React.Fragment>
      );
    });
  };

  return (
    <Drawer anchor="left" open={isOpen} onClose={onClose}>
      <Box
        role="presentation"
        sx={{
          width: 250,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <img src={CompanyLogo} alt="Company Logo" style={{ maxWidth: "120px" }} />
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <List>{renderMenuItems(menuItems)}</List>
        </Box>

        <Box sx={{ p: 2, flexShrink: 0 }}>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
            }}
            onClick={handleLogOutMenu}
          >
            <LogoutIcon sx={{ color: "#DB4437", width: "24px", height: "24px" }} />
            <Typography sx={{ color: "#DB4437", fontSize: "14px", fontWeight: "400" }}>
              Logout
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileDrawer;
